import { Component, Vue, Watch } from "vue-property-decorator";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import TaxonomyValue from "@/interfaces/TaxonomyValue";
import {
  WeekPickerResult,
  WEEK_PICKER_MODALITY_WEEK_RANGE,
} from "@/interfaces/WeekPickerDataTypes";
import { ComparisonGroup } from "@/models";
import GeographicAreasService from "@/services/GeographicAreasService";
import Area from "@/models/Area";
import ModuleTypologiesService from "@/services/ModuleTypologiesService";
import ModuleTypology from "@/models/ModuleTypology";
import SpecialInitiativesService from "@/services/SpecialInitiavesService";
import Throttler from "@/services/Throttler";
import QueryHelper from "@/services/QueryHelper";
import moment from "moment";

type TaxonomyFieldName = keyof TaxonomyValue;
type ExtendedTaxonomyFieldName =
  | TaxonomyFieldName
  | "subjectClass"
  | "subjectSector"
  | "subjectCategory";

export type FilterType = {
  heatmap?: string;
  years?: string;
  collapse?: string;
  marketSubject?: string;
  clientSubject?: string;
  exclusivity?: string;
  unitOfMeasurement?: string;
  area?: string;
  businessSector?: string;
  moduleTypology?: string;
  weekRange?: string;
  sector?: string;
  category?: string;
  class?: string;
  subjectSector?: string;
  subjectCategory?: string;
  subjectClass?: string;
  subjectGroup?: string;
  group?: string;
  subjectKind?: string;
  subjectIds?: string;
  granularity?: string;
  timeGranularity?: string;
};
type FieldName = keyof FilterType;

@Component
export default class FiltersMixin extends Vue {
  granularityEntries: MultiLevelObject[] = [
    {
      id: "company",
      text: "Azienda",
    },
    {
      id: "brand",
      text: "Brand",
    },
    {
      id: "product",
      text: "Prodotto",
    },
  ];

  clientSubjectsEntries: MultiLevelObject[] = [
    {
      id: "taxonomies",
      text: "Settore/Categoria/Classe",
    },
    {
      id: "groups",
      text: "Gruppi",
    },
  ];

  marketSubjectsEntries: MultiLevelObject[] = [
    {
      id: "market_segments",
      text: "Segmenti di mercato",
    },
    {
      id: "sales_agencies",
      text: "Concessionarie",
    },
    {
      id: "broadcasters",
      text: "Emittenti",
    },
    {
      id: "editors",
      text: "Editori",
    },
  ];

  exclusivitiesEntries: MultiLevelObject[] = [
    {
      id: "all",
      text: "Tutti",
    },
    {
      id: "my_exclusivity_type",
      text: "Esclusivi miei",
    },
    {
      id: "others_exclusivity_type",
      text: "Esclusivi concorrenza",
    },
    {
      id: "shared_exclusivity_type",
      text: "Comuni",
    },
  ];
  unitOfMeasurementsEntries: MultiLevelObject[] = [
    {
      id: "quoted_seconds",
      text: "Secondi (quote)",
    },
    {
      id: "quoted_spots",
      text: "Spot (quote)",
    },
    {
      id: "seconds",
      text: "Secondi",
    },
    {
      id: "spots",
      text: "Spot",
    },
    {
      id: "clients_number",
      text: "N° clienti",
    },
  ];
  clientUnitOfMeasurementsEntries: MultiLevelObject[] = [
    {
      id: "quoted_seconds",
      text: "Secondi (quote)",
    },
    {
      id: "quoted_spots",
      text: "Spot (quote)",
    },
  ];
  productUnitOfMeasurementsEntries: MultiLevelObject[] = [
    {
      id: "seconds",
      text: "Secondi",
    },
    {
      id: "spots",
      text: "Spot",
    },
  ];

  trendUnitOfMeasurementsEntries: MultiLevelObject[] = [
    {
      id: "seconds",
      text: "Secondi",
    },
    {
      id: "spots",
      text: "Spot",
    },
  ];

  timeGranularityShortRanged: MultiLevelObject[] = [
    {
      id: "month",
      text: "Mese",
    },
    {
      id: "quarter",
      text: "Trimestre",
    },
    {
      id: "semester",
      text: "Semestre",
    },
  ];

  timeGranularityLongRanged: MultiLevelObject[] = [
    {
      id: "quarter",
      text: "Trimestre",
    },
    {
      id: "semester",
      text: "Semestre",
    },
    {
      id: "year",
      text: "Anno",
    },
  ];

  timeGranularityRanges: MultiLevelObject[] = [
    {
      id: "month",
      text: "Mese",
    },
    {
      id: "quarter",
      text: "Trimestre",
    },
    {
      id: "semester",
      text: "Semestre",
    },
    {
      id: "year",
      text: "Anno",
    },
  ];

  compareYearsRange = 7;

  yearsToCompare: string[] | null = null;
  exclusivities = this.exclusivitiesEntries;
  uoms = this.unitOfMeasurementsEntries;

  clientSubject: string | null = "taxonomies";
  marketSubject: string | null = "market_segments";
  collapse = false;
  heatmap = false;
  exclusivity = "all";
  unitOfMeasurement = "quoted_seconds";
  weekRange: WeekPickerResult = null;
  group: ComparisonGroup | null = null;

  areas: MultiLevelObject[] = [];
  area: string | null = null;
  moduleTypology: string | null = null;
  moduleTypologies: MultiLevelObject[] = [];
  taxonomies: TaxonomyValue = {
    sector: "",
    category: "",
    class: "",
  };
  subjectTaxonomies: TaxonomyValue = {
    sector: "",
    category: "",
    class: "",
  };

  filters: FilterType = {};

  advancedFiltersShow = false;

  throttler = new Throttler(100);

  refresh = 0;
  forceReloadFromApi = false;

  subjectKind: { id: string; text: string } | null = null;
  subjectIds: string[] = [];
  granularity = "company";
  timeGranularity = "month";

  updateNavigation() {
    this.throttler.run(() => {
      if (QueryHelper.isSameQuery(this.$route.query, this.filters)) {
        return;
      }
      this.$router.replace({ query: { ...this.filters } });
    });
  }

  advancedFiltersToggle() {
    this.advancedFiltersShow = !this.advancedFiltersShow;
  }

  resetAdvancedFilters() {
    this.advancedFiltersShow = false;
    this.area = null;
    this.moduleTypology = null;
    this.taxonomies = {
      sector: [],
      category: [],
      class: [],
    };

    this.applyAdvancedFilters();
  }

  async initFilters() {
    this.initCollapseFilters();
    this.initHeatmapFilters();
    this.initSubjectFilters();
    this.initExclusivityFilters();
    this.initUnitOfMeasurementFilters();
    await this.initWeekRangeFilters();
    this.initTaxonomyFilters();
    this.initSubjectTaxonomyFilters();
    await this.initGeographicAreasOptions();
    await this.initModuleTypologiesOptions();
    this.initYearsFilters();
    this.initAreaFilters();
    this.initModuleTypologyFilters();
    this.refresh = this.$moment().valueOf();
    this.initSubjectKindFilter();
    this.initSubjectIdsFilter();
    this.initGranularityFilters();
    this.initTimeGranularityFilters();
  }

  @Watch("yearsToCompare")
  onYearsToCompareChanged(value: string[] | null) {
    const before = this.filters.years;
    delete this.filters.years;
    if (value && value.length > 0) {
      this.filters = Object.assign({}, this.filters, {
        years: value.join(","),
      });
    }
    if (before != this.filters.years) {
      this.forceReloadFromApi = true || this.forceReloadFromApi;
      this.updateNavigation();
    }
  }

  @Watch("collapse")
  onCollapseChanged(value: boolean) {
    const before = this.filters.collapse;
    delete this.filters.collapse;
    if (value) {
      this.filters = Object.assign({}, this.filters, { collapse: "true" });
    }
    if (before != this.filters.collapse) {
      this.updateNavigation();
    }
  }

  @Watch("heatmap")
  onHeatmapChanged(value: boolean) {
    const before = this.filters.heatmap;
    delete this.filters.heatmap;
    if (value) {
      this.filters = Object.assign({}, this.filters, { heatmap: "true" });
    }
    if (before != this.filters.heatmap) {
      this.updateNavigation();
    }
  }

  onEncodedFilterChanged(
    value: string | null,
    fieldName: FieldName,
    entries: MultiLevelObject[]
  ) {
    const before = this.filters[fieldName];
    delete this.filters[fieldName];
    if (value) {
      const entry = entries.find((s) => s.id == value);
      if (entry) {
        const filterEntry: { [key: string]: string } = {};
        filterEntry[fieldName] = encodeURIComponent(entry.text);
        this.filters = Object.assign({}, this.filters, filterEntry);
      }
    }
    return before != this.filters[fieldName];
  }

  @Watch("marketSubject")
  onMarketSubjectChanged(value: string) {
    if (
      this.onEncodedFilterChanged(
        value,
        "marketSubject",
        this.marketSubjectsEntries
      )
    ) {
      this.updateNavigation();
    }
  }

  @Watch("clientSubject")
  onClientSubjectChanged(value: string) {
    if (
      this.onEncodedFilterChanged(
        value,
        "clientSubject",
        this.clientSubjectsEntries
      )
    ) {
      this.subjectTaxonomies = {
        sector: "",
        category: "",
        class: "",
      };
      this.group = null;
      this.updateNavigation();
    }
  }

  @Watch("exclusivity")
  onExclusivityChanged(value: string) {
    if (this.onEncodedFilterChanged(value, "exclusivity", this.exclusivities)) {
      this.updateNavigation();
    }
  }

  @Watch("unitOfMeasurement")
  onUnitOfMeasurementChanged(value: string) {
    if (this.onEncodedFilterChanged(value, "unitOfMeasurement", this.uoms)) {
      this.updateNavigation();
    }
  }

  @Watch("granularity")
  onGranularityChanged(value: string) {
    if (
      this.onEncodedFilterChanged(value, "granularity", this.granularityEntries)
    ) {
      this.forceReloadFromApi = true || this.forceReloadFromApi;
      this.updateNavigation();
    }
  }

  @Watch("timeGranularity")
  onTimeGranularityChanged(value: string) {
    if (
      this.onEncodedFilterChanged(
        value,
        "timeGranularity",
        this.timeGranularityRanges
      )
    ) {
      this.forceReloadFromApi = true || this.forceReloadFromApi;
      this.updateNavigation();
    }
  }

  @Watch("weekRange")
  onWeekRangeChanged(value: WeekPickerResult) {
    const before = this.filters.weekRange;
    delete this.filters.weekRange;
    if (value && value.type === WEEK_PICKER_MODALITY_WEEK_RANGE) {
      this.filters.weekRange = `${value.start.week},${value.start.year},${value.end.week},${value.end.year}`;
    }
    if (before != this.filters.weekRange) {
      this.forceReloadFromApi = !!before || this.forceReloadFromApi;
      this.updateNavigation();
    }
  }

  onTaxonomiesChanged(value: TaxonomyValue): boolean {
    return (
      this.onGenericTaxonomyChanged("class", "class", value) ||
      this.onGenericTaxonomyChanged("sector", "sector", value) ||
      this.onGenericTaxonomyChanged("category", "category", value)
    );
  }

  @Watch("subjectTaxonomies", { deep: true })
  onSubjectTaxonomiesChanged(value: TaxonomyValue) {
    const classBefore = this.onGenericTaxonomyChanged(
      "subjectClass",
      "class",
      value
    );
    const sectorBefore = this.onGenericTaxonomyChanged(
      "subjectSector",
      "sector",
      value
    );
    const categoryBefore = this.onGenericTaxonomyChanged(
      "subjectCategory",
      "category",
      value
    );
    if (classBefore || sectorBefore || categoryBefore) {
      if (this.clientSubject === "taxonomies") {
        this.forceReloadFromApi = true || this.forceReloadFromApi;
        this.updateNavigation();
      }
    }
  }

  onGenericTaxonomyChanged(
    fieldName: ExtendedTaxonomyFieldName,
    taxonomyField: TaxonomyFieldName,
    value: TaxonomyValue
  ): boolean {
    const before = this.filters[fieldName];
    delete this.filters[fieldName];

    if (value[taxonomyField] && value[taxonomyField] !== "") {
      this.filters[fieldName] = value[taxonomyField] as string;
    }

    return before != this.filters[fieldName];
  }

  @Watch("group", { deep: true })
  onGroupChanged(value: ComparisonGroup | null) {
    const before = this.filters.group;
    delete this.filters.group;
    if (value) {
      this.filters.group = value.id;
    }
    if (before != this.filters.group) {
      if (this.clientSubject === "groups") {
        this.forceReloadFromApi = true || this.forceReloadFromApi;
        this.updateNavigation();
      }
    }
  }

  @Watch("subjectKind", { deep: true })
  onSubjectKindChanged(value: { id: string; text: string } | null) {
    const before = this.filters.subjectKind;
    delete this.filters.subjectKind;
    if (value) {
      this.filters.subjectKind = value.id;
    }
    if (before !== this.filters.subjectKind) {
      this.updateNavigation();
    }
  }

  onKindSelected(kind: any) {
    this.subjectKind = kind;
  }

  @Watch("subjectIds", { deep: true })
  onSubjectIdsChanged(value: string[]) {
    const before = this.filters.subjectIds;
    delete this.filters.subjectIds;
    if (value.length > 0) {
      this.filters.subjectIds = value.join(",");
    }
    if (before !== this.filters.subjectIds) {
      this.forceReloadFromApi = true || this.forceReloadFromApi;
      this.updateNavigation();
    }
  }

  applyAdvancedFilters() {
    const beforeArea = this.onEncodedFilterChanged(
      this.area,
      "area",
      this.areas
    );
    const beforeModule = this.onEncodedFilterChanged(
      this.moduleTypology,
      "moduleTypology",
      this.moduleTypologies
    );
    const beforeTaxonomy = this.onTaxonomiesChanged(this.taxonomies);
    if (beforeArea || beforeModule || beforeTaxonomy) {
      this.forceReloadFromApi = this.forceReloadFromApi || true;
      this.updateNavigation();
    }
  }

  async initGeographicAreasOptions() {
    const geographicAreas: Area[] =
      await GeographicAreasService.getGeographicAreas();
    this.areas = geographicAreas.map((area) => {
      return {
        id: area.id,
        text: area.nome,
      };
    });
  }

  async initModuleTypologiesOptions() {
    const typologies: ModuleTypology[] =
      await ModuleTypologiesService.getModuleTypologies();
    this.moduleTypologies = typologies.map((typology) => {
      return {
        id: typology.id,
        text: ModuleTypology.humanName(typology.name),
      };
    });
  }

  initYearsFilters() {
    if (this.$route.query.years) {
      this.filters.years = this.$route.query.years as string;
      this.yearsToCompare = this.filters.years.split(",");
    }
  }

  initCollapseFilters() {
    if (this.$route.query.collapse) {
      this.filters.collapse = "true";
      this.collapse = true;
    }
  }

  initHeatmapFilters() {
    if (this.$route.query.heatmap) {
      this.filters.heatmap = "true";
      this.heatmap = true;
    }
  }

  initEncodedFieldFilters(fieldName: FieldName, entries: MultiLevelObject[]) {
    if (this.$route.query[fieldName]) {
      this.filters[fieldName] = this.$route.query[fieldName] as string;
      const search = decodeURIComponent(this.filters[fieldName] || "");
      const entry = entries.find((s) => s.text == search);
      if (entry) {
        switch (fieldName) {
          case "clientSubject":
          case "marketSubject":
          case "exclusivity":
          case "unitOfMeasurement":
          case "area":
          case "moduleTypology":
          case "granularity":
          case "timeGranularity":
            this[fieldName] = entry.id;
            break;
        }
      }
    }
  }

  initSubjectFilters() {
    this.initEncodedFieldFilters("marketSubject", this.marketSubjectsEntries);
    this.initEncodedFieldFilters("clientSubject", this.clientSubjectsEntries);
  }

  initExclusivityFilters() {
    this.initEncodedFieldFilters("exclusivity", this.exclusivities);
  }

  initUnitOfMeasurementFilters() {
    this.initEncodedFieldFilters("unitOfMeasurement", this.uoms);
  }

  initGranularityFilters() {
    this.initEncodedFieldFilters("granularity", this.granularityEntries);
  }

  initTimeGranularityFilters() {
    this.initEncodedFieldFilters("timeGranularity", this.timeGranularityRanges);
  }

  initAreaFilters() {
    this.initEncodedFieldFilters("area", this.areas);
  }

  initModuleTypologyFilters() {
    this.initEncodedFieldFilters("moduleTypology", this.moduleTypologies);
  }

  async initWeekRangeFilters() {
    if (this.$route.query.weekRange) {
      this.filters.weekRange = this.$route.query.weekRange as string;
      const splittedFilter = this.filters.weekRange.split(",");
      this.weekRange = {
        start: {
          week: Number.parseInt(splittedFilter[0]),
          year: Number.parseInt(splittedFilter[1]),
        },
        end: {
          week: Number.parseInt(splittedFilter[2]),
          year: Number.parseInt(splittedFilter[3]),
        },
        type: WEEK_PICKER_MODALITY_WEEK_RANGE,
      };
    } else {
      const date = await SpecialInitiativesService.lastPublishedDate();
      const parsedDate = this.$moment(date.date);
      if (this.$route.name === "SpecialInitiativesTrend") {
        const weeks = this.computeWeeksInMonth(parsedDate);
        const year = parsedDate.isoWeekYear();
        const startWeek = weeks[0];
        const endWeek = weeks.pop() || 0;
        this.filters.weekRange = `${startWeek},${year},${endWeek},${year}`;
        this.weekRange = {
          start: {
            week: startWeek,
            year,
          },
          end: {
            week: endWeek,
            year,
          },
          type: WEEK_PICKER_MODALITY_WEEK_RANGE,
        };
      } else {
        const week = parsedDate.isoWeek();
        const year = parsedDate.isoWeekYear();
        this.filters.weekRange = `${week},${year},${week},${year}`;
        this.weekRange = {
          start: {
            week,
            year,
          },
          end: {
            week,
            year,
          },
          type: WEEK_PICKER_MODALITY_WEEK_RANGE,
        };
      }
    }
  }

  private computeWeeksInMonth(date: moment.Moment): number[] {
    const month = date.month();
    const start = date.startOf("month").clone();
    const end = date.endOf("month").clone();
    const response: number[] = [];
    for (
      let momentStart = start.isoWeekday(4);
      momentStart.isSameOrBefore(end);
      momentStart = momentStart.add(1, "week")
    ) {
      const rweek = momentStart.isoWeek();
      const rmonth = moment()
        .isoWeekYear(momentStart.isoWeekYear())
        .isoWeek(rweek)
        .isoWeekday(4);
      if (rmonth.month() !== month) {
        continue;
      }
      response.push(rweek);
    }
    return response;
  }

  initTaxonomyFilters() {
    if (this.$route.query.class) {
      this.initGenericTaxonomyFilter(
        "class",
        "class",
        this.$route.query.class as string
      );
    }
    if (this.$route.query.sector) {
      this.initGenericTaxonomyFilter(
        "sector",
        "sector",
        this.$route.query.sector as string
      );
    }
    if (this.$route.query.category) {
      this.initGenericTaxonomyFilter(
        "category",
        "category",
        this.$route.query.category as string
      );
    }
  }

  initGenericTaxonomyFilter(
    fieldName: ExtendedTaxonomyFieldName,
    taxonomyFieldName: TaxonomyFieldName,
    value: string
  ) {
    this.filters[fieldName] = value;
    this.subjectTaxonomies[taxonomyFieldName] = value;
  }

  initSubjectTaxonomyFilters() {
    if (this.$route.query.subjectClass) {
      this.initGenericTaxonomyFilter(
        "subjectClass",
        "class",
        this.$route.query.subjectClass as string
      );
    }
    if (this.$route.query.subjectSector) {
      this.initGenericTaxonomyFilter(
        "subjectSector",
        "sector",
        this.$route.query.subjectSector as string
      );
    }
    if (this.$route.query.subjectCategory) {
      this.initGenericTaxonomyFilter(
        "subjectCategory",
        "category",
        this.$route.query.subjectCategory as string
      );
    }
  }

  initSubjectKindFilter() {
    if (this.$route.query.subjectKind) {
      this.filters.subjectKind = this.$route.query.subjectKind as string;
      this.subjectKind = { id: this.filters.subjectKind, text: "" };
    }
  }

  initSubjectIdsFilter() {
    if (this.$route.query.subjectIds) {
      this.filters.subjectIds = this.$route.query.subjectIds as string;
      this.subjectIds = this.filters.subjectIds?.split(",") || [];
    }
  }

  filtersForAPI(kind: "client" | "market" | "top_investors" | "trend") {
    const filters = {
      weekRange: this.filters.weekRange || null,
      area: this.area,
      moduleTypology: this.moduleTypology,
      marketSubject: this.marketSubject,
      clientSubject: this.clientSubject,
      subjectSector: this.subjectTaxonomies.sector,
      subjectCategory: this.subjectTaxonomies.category,
      subjectClass: this.subjectTaxonomies.class,
      sector: this.taxonomies.sector,
      category: this.taxonomies.category,
      class: this.taxonomies.class,
      group: this.group,
      subjectKind: this.subjectKind,
      subjectIds: this.subjectIds,
      granularity: this.granularity,
      timeGranularity: this.timeGranularity,
      unitOfMeasurement: this.unitOfMeasurement || null,
    };
    if (kind === "client") {
      filters.marketSubject = null;
    } else if (kind === "market" || kind === "trend") {
      filters.subjectSector = "";
      filters.subjectCategory = "";
      filters.subjectClass = "";
      filters.group = null;
    } else {
      filters.marketSubject = null;
      filters.subjectSector = "";
      filters.subjectCategory = "";
      filters.subjectClass = "";
      filters.group = null;
    }
    if (kind !== "top_investors") {
      filters.unitOfMeasurement = null;
    }
    return filters;
  }
}
